import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { X, Upload, Search, Timer, User, FileText } from "lucide-react";

import {
  SectionTitle,
  T,
  currencySymbol,
  formatDateTime,
  ghostButtonSx,
  primaryButtonSx,
} from "./shared";
import {
  createComplaint,
  updateComplaint,
  lookupOrder,
  uploadAttachments,
  errorMessage,
} from "./api";

const EMPTY = {
  customer: { customerId: "", name: "", mobile: "", email: "" },
  order: {
    orderId: "",
    restaurantId: "",
    restaurantName: "",
    deliveryPartnerId: "",
    deliveryPartnerName: "",
    orderDateTime: "",
    orderAmount: "",
    paymentMode: "",
  },
  location: { city: "", region: "", pincode: "" },
  source: "",
  categoryId: "",
  subCategory: "",
  description: "",
  priority: "",
  assignToSelf: false,
};

/** Datetime-local inputs need "YYYY-MM-DDTHH:mm", not an ISO string. */
const toLocalInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

/**
 * Complaint registration / edit dialog.
 * @param {object} meta form metadata from /complaints/meta/form
 * @param {object|null} complaint when set, the dialog edits instead of creating
 */
const ComplaintForm = ({ open, onClose, onSaved, meta, complaint = null }) => {
  const isEdit = Boolean(complaint);

  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [lookupState, setLookupState] = useState({ loading: false, message: "" });

  // Reset whenever the dialog opens, so a cancelled draft never leaks into the
  // next complaint.
  useEffect(() => {
    if (!open) return;

    setError("");
    setFiles([]);
    setLookupState({ loading: false, message: "" });

    if (complaint) {
      setForm({
        customer: {
          customerId: complaint.customer?.customerId || "",
          name: complaint.customer?.name || "",
          mobile: complaint.customer?.mobile || "",
          email: complaint.customer?.email || "",
        },
        order: {
          orderId: complaint.order?.orderId || "",
          restaurantId: complaint.order?.restaurantId || "",
          restaurantName: complaint.order?.restaurantName || "",
          deliveryPartnerId: complaint.order?.deliveryPartnerId || "",
          deliveryPartnerName: complaint.order?.deliveryPartnerName || "",
          orderDateTime: toLocalInput(complaint.order?.orderDateTime),
          orderAmount: complaint.order?.orderAmount ?? "",
          paymentMode: complaint.order?.paymentMode || "",
        },
        location: {
          city: complaint.location?.city || "",
          region: complaint.location?.region || "",
          pincode: complaint.location?.pincode || "",
        },
        source: complaint.source || "",
        categoryId: complaint.categoryId || "",
        subCategory: complaint.subCategory || "",
        description: complaint.description || "",
        priority: complaint.priority || "",
        assignToSelf: false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, complaint]);

  const categories = useMemo(() => meta?.categories || [], [meta]);
  const selectedCategory = useMemo(
    () => categories.find((c) => String(c._id) === String(form.categoryId)),
    [categories, form.categoryId]
  );

  const setField = (path, value) => {
    setForm((prev) => {
      const [group, key] = path.split(".");
      if (key) return { ...prev, [group]: { ...prev[group], [key]: value } };
      return { ...prev, [group]: value };
    });
  };

  // Picking a category pre-selects its default priority and clears a
  // sub-category that no longer belongs.
  const handleCategoryChange = (categoryId) => {
    const category = categories.find((c) => String(c._id) === String(categoryId));
    setForm((prev) => ({
      ...prev,
      categoryId,
      subCategory: "",
      priority: prev.priority || category?.defaultPriority || "Medium",
    }));
  };

  /** Live preview of the SLA the complaint will be created with. */
  const slaPreview = useMemo(() => {
    if (!selectedCategory) return null;

    const priority = form.priority || selectedCategory.defaultPriority || "Medium";
    const matrix = selectedCategory.slaMatrix || {};
    const target = matrix[priority];
    if (!target) return null;

    const now = Date.now();
    return {
      priority,
      firstResponseDueAt: new Date(now + target.firstResponseMins * 60000),
      resolutionDueAt: new Date(now + target.resolutionMins * 60000),
    };
  }, [selectedCategory, form.priority]);

  const handleOrderLookup = async () => {
    const orderId = form.order.orderId?.trim();
    if (!orderId) return;

    setLookupState({ loading: true, message: "" });
    try {
      const result = await lookupOrder(orderId);
      if (!result?.found) {
        setLookupState({
          loading: false,
          message: "No earlier complaint on this order — enter the details below.",
        });
        return;
      }

      setForm((prev) => ({
        ...prev,
        order: {
          ...prev.order,
          restaurantId: result.order?.restaurantId || prev.order.restaurantId,
          restaurantName: result.order?.restaurantName || prev.order.restaurantName,
          deliveryPartnerId: result.order?.deliveryPartnerId || prev.order.deliveryPartnerId,
          deliveryPartnerName:
            result.order?.deliveryPartnerName || prev.order.deliveryPartnerName,
          orderDateTime: toLocalInput(result.order?.orderDateTime) || prev.order.orderDateTime,
          orderAmount: result.order?.orderAmount ?? prev.order.orderAmount,
          paymentMode: result.order?.paymentMode || prev.order.paymentMode,
        },
        customer: {
          customerId: result.customer?.customerId || prev.customer.customerId,
          name: result.customer?.name || prev.customer.name,
          mobile: result.customer?.mobile || prev.customer.mobile,
          email: result.customer?.email || prev.customer.email,
        },
        location: {
          city: result.location?.city || prev.location.city,
          region: result.location?.region || prev.location.region,
          pincode: result.location?.pincode || prev.location.pincode,
        },
      }));

      setLookupState({
        loading: false,
        message: `Details filled from an earlier complaint. This order has ${result.previousComplaints} complaint(s) already.`,
      });
    } catch (err) {
      setLookupState({ loading: false, message: errorMessage(err, "Order lookup failed") });
    }
  };

  const validate = () => {
    if (!form.source) return "Select where this complaint came from";
    if (!form.categoryId) return "Select a complaint category";
    if (!form.description.trim()) return "Describe the complaint";
    if (!form.customer.mobile && !form.customer.email && !form.customer.customerId) {
      return "Enter at least one customer identifier (ID, mobile or email)";
    }
    return "";
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      ...form,
      order: {
        ...form.order,
        orderAmount: form.order.orderAmount === "" ? 0 : Number(form.order.orderAmount),
        orderDateTime: form.order.orderDateTime || null,
        paymentMode: form.order.paymentMode || null,
      },
    };

    try {
      const response = isEdit
        ? await updateComplaint(complaint._id, payload)
        : await createComplaint(payload);

      // Attachments need the complaint to exist first, so they upload after.
      const savedId = response?.data?._id || complaint?._id;
      if (files.length && savedId) {
        try {
          await uploadAttachments(savedId, files);
        } catch (uploadErr) {
          // The complaint itself saved — say so rather than failing the whole
          // submission over an attachment.
          onSaved?.(
            `${response?.message || "Complaint saved"}, but attachments failed to upload: ${errorMessage(
              uploadErr
            )}`
          );
          onClose();
          return;
        }
      }

      onSaved?.(response?.message || (isEdit ? "Complaint updated" : "Complaint registered"));
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Failed to save complaint"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: T.radiusCard, overflow: "hidden" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1.5 }}>
        <Box>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: T.ink }}>
            {isEdit ? `Edit ${complaint.ticketId}` : "Register Complaint"}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: T.inkSecondary, mt: 0.25 }}>
            {isEdit
              ? "Update the complaint details"
              : "A ticket ID and SLA are generated automatically on save"}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} size="small">
          <X size={18} color={T.inkSecondary} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: T.border, bgcolor: T.page }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: T.radiusControl }}>
            {error}
          </Alert>
        )}

        {/* ---- Customer & order ---- */}
        <Box sx={{ bgcolor: T.surface, borderRadius: T.radiusCard, border: `1px solid ${T.border}`, p: 2.5, mb: 2 }}>
        <SectionTitle icon={User}>Customer &amp; Order Details</SectionTitle>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Customer ID"
              value={form.customer.customerId}
              onChange={(e) => setField("customer.customerId", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Mobile Number"
              value={form.customer.mobile}
              onChange={(e) => setField("customer.mobile", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Email" type="email"
              value={form.customer.email}
              onChange={(e) => setField("customer.email", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Customer Name"
              value={form.customer.name}
              onChange={(e) => setField("customer.name", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth size="small" label="Order ID"
              value={form.order.orderId}
              onChange={(e) => setField("order.orderId", e.target.value)}
              onBlur={handleOrderLookup}
              helperText={lookupState.message || "Details auto-fill if this order was complained about before"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleOrderLookup} disabled={lookupState.loading}>
                      {lookupState.loading ? <CircularProgress size={16} /> : <Search size={16} color={T.inkSecondary} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Restaurant Name"
              value={form.order.restaurantName}
              onChange={(e) => setField("order.restaurantName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Restaurant ID"
              value={form.order.restaurantId}
              onChange={(e) => setField("order.restaurantId", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Delivery Partner ID"
              value={form.order.deliveryPartnerId}
              onChange={(e) => setField("order.deliveryPartnerId", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Delivery Partner Name"
              value={form.order.deliveryPartnerName}
              onChange={(e) => setField("order.deliveryPartnerName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Order Date &amp; Time" type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={form.order.orderDateTime}
              onChange={(e) => setField("order.orderDateTime", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Order Amount" type="number"
              value={form.order.orderAmount}
              onChange={(e) => setField("order.orderAmount", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{currencySymbol(meta?.currency)}</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select fullWidth size="small" label="Payment Mode"
              value={form.order.paymentMode}
              onChange={(e) => setField("order.paymentMode", e.target.value)}
            >
              <MenuItem value="">—</MenuItem>
              {(meta?.paymentModes || []).map((mode) => (
                <MenuItem key={mode} value={mode}>{mode}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="City"
              value={form.location.city}
              onChange={(e) => setField("location.city", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Region"
              value={form.location.region}
              onChange={(e) => setField("location.region", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth size="small" label="Pincode"
              value={form.location.pincode}
              onChange={(e) => setField("location.pincode", e.target.value)}
            />
          </Grid>
        </Grid>
        </Box>

        {/* ---- Complaint ---- */}
        <Box sx={{ bgcolor: T.surface, borderRadius: T.radiusCard, border: `1px solid ${T.border}`, p: 2.5 }}>
        <SectionTitle icon={FileText}>Complaint Details</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              select required fullWidth size="small" label="Complaint Source"
              value={form.source}
              onChange={(e) => setField("source", e.target.value)}
            >
              {(meta?.sources || []).map((source) => (
                <MenuItem key={source} value={source}>{source}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select required fullWidth size="small" label="Category"
              value={form.categoryId}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {categories.map((category) => (
                <MenuItem key={category._id} value={category._id}>{category.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select fullWidth size="small" label="Sub-category"
              value={form.subCategory}
              onChange={(e) => setField("subCategory", e.target.value)}
              disabled={!selectedCategory}
              helperText={!selectedCategory ? "Select a category first" : " "}
            >
              <MenuItem value="">—</MenuItem>
              {(selectedCategory?.subCategories || []).map((sub) => (
                <MenuItem key={sub} value={sub}>{sub}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              required fullWidth multiline minRows={3} size="small"
              label="Complaint Description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select fullWidth size="small" label="Priority"
              value={form.priority}
              onChange={(e) => setField("priority", e.target.value)}
              helperText={
                selectedCategory ? `Category default: ${selectedCategory.defaultPriority}` : " "
              }
            >
              {(meta?.priorities || []).map((priority) => (
                <MenuItem key={priority} value={priority}>{priority}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Box
              sx={{
                bgcolor: slaPreview ? T.brandSoft : T.surfaceMuted,
                border: `1px solid ${slaPreview ? "#E0E3FB" : T.border}`,
                borderRadius: T.radiusControl,
                p: 1.75,
                height: "100%",
                display: "flex",
                gap: 1.25,
              }}
            >
              <Timer size={16} color={slaPreview ? T.brand : T.inkMuted} style={{ flexShrink: 0, marginTop: 2 }} />
              <Box>
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: T.inkSecondary, textTransform: "uppercase", letterSpacing: "0.3px" }}>
                  SLA target (auto-calculated)
                </Typography>
                {slaPreview ? (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography sx={{ fontSize: "0.76rem", color: T.ink }}>
                      First response by <strong>{formatDateTime(slaPreview.firstResponseDueAt)}</strong>
                    </Typography>
                    <Typography sx={{ fontSize: "0.76rem", color: T.ink }}>
                      Resolution by <strong>{formatDateTime(slaPreview.resolutionDueAt)}</strong>
                    </Typography>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: "0.76rem", color: T.inkSecondary, mt: 0.5 }}>
                    Select a category and priority to see the SLA target.
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* ---- Attachments ---- */}
          <Grid item xs={12}>
            <Button
              component="label"
              variant="outlined"
              size="small"
              startIcon={<Upload size={15} />}
              sx={ghostButtonSx}
            >
              Attach proof (photo / video)
              <input
                hidden
                multiple
                type="file"
                accept="image/*,video/*,.pdf"
                onChange={(e) => setFiles(Array.from(e.target.files).slice(0, 5))}
              />
            </Button>
            <Typography sx={{ fontSize: "0.72rem", color: T.inkMuted, ml: 1 }} component="span">
              Up to 5 files
            </Typography>

            {files.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.25 }}>
                {files.map((file, index) => (
                  <Chip
                    key={`${file.name}-${index}`}
                    label={file.name}
                    size="small"
                    onDelete={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                    sx={{
                      borderRadius: T.radiusControl,
                      bgcolor: T.surfaceMuted,
                      border: `1px solid ${T.border}`,
                      maxWidth: 240,
                    }}
                  />
                ))}
              </Box>
            )}
          </Grid>

          {!isEdit && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.assignToSelf}
                    onChange={(e) => setField("assignToSelf", e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Assign this complaint to me (otherwise it goes to the category queue)
                  </Typography>
                }
              />
            </Grid>
          )}
        </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: T.inkSecondary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={15} color="inherit" /> : null}
          sx={primaryButtonSx}
        >
          {saving ? "Saving..." : isEdit ? "Save Changes" : "Register Complaint"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComplaintForm;
